import App from "@/App.vue";
import Icon from "@/components/Icon.vue";
import "@/less/index.less";
import router from "@/router";
import DataStore from "@/store/DataStore";
import * as StoreProxy from "@/store/StoreProxy";
import { storeAccessibility } from "@/store/accessibility/storeAccessibility";
import { storeAccount } from "@/store/account/storeAccount";
import { storeAdmin } from "@/store/admin/storeAdmin";
import { storeAuth } from "@/store/auth/storeAuth";
import { storeAutomod } from "@/store/automod/storeAutomod";
import { storeBingo } from "@/store/bingo/storeBingo";
import { storeBingoGrid } from "@/store/bingo_grid/storeBingoGrid";
import { storeChat } from "@/store/chat/storeChat";
import { storeChatSuggestion } from "@/store/chat_sugg/storeChatSuggestion";
import { storeCounters } from "@/store/counters/storeCounters";
import { storeDebug } from "@/store/debug/storeDebug";
import { storeDiscord } from "@/store/discord/storeDiscord";
import { storeEmergency } from "@/store/emergency/storeEmergency";
import { storeExtension } from "@/store/extension/storeExtension";
import { storeHeat } from "@/store/heat/storeHeat";
import { storeKofi } from "@/store/kofi/storeKofi";
import { storeLumia } from "@/store/lumia/storeLumia";
import { storeMusic } from "@/store/music/storeMusic";
import { storeOBS } from "@/store/obs/storeOBS";
import { storeParams } from "@/store/params/storeParams";
import { storePatreon } from "@/store/patreon/storePatreon";
import { storePoll } from "@/store/poll/storePoll";
import { storePrediction } from "@/store/prediction/storePrediction";
import { storeQna } from "@/store/qna/storeQna";
import { storeRaffle } from "@/store/raffle/storeRaffle";
import { storeRewards } from "@/store/rewards/storeRewards";
import { storeMain } from "@/store/storeMain";
import { storeStream } from "@/store/stream/storeStream";
import { storeStreamelements } from "@/store/streamelements/storeStreamelements";
import { storeStreamlabs } from "@/store/streamlabs/storeStreamlabs";
import { storeTimer } from "@/store/timer/storeTimer";
import { storeTipeee } from "@/store/tipeee/storeTipeee";
import { storeTriggers } from "@/store/triggers/storeTriggers";
import { storeTTS } from "@/store/tts/storeTTS";
import { storeUsers } from "@/store/users/storeUsers";
import { storeValues } from "@/store/values/storeValues";
import { storeVoice } from "@/store/voice/storeVoice";
import { storeYoutube } from "@/store/youtube/storeYoutube";
import type { TwitchatDataTypes } from "@/types/TwitchatDataTypes";
import ContextMenu from "@imengyu/vue3-context-menu";
import "@imengyu/vue3-context-menu/lib/vue3-context-menu.css";
import { createPopper } from "@popperjs/core";
import * as Sentry from "@sentry/vue";
import CSSPlugin from "gsap/CSSPlugin";
import { CustomEase, ScrollToPlugin } from "gsap/all";
import { gsap } from "gsap/gsap-core";
import { createPinia } from "pinia";
import "tippy.js/animations/scale.css";
import "tippy.js/dist/tippy.css";
import { createApp, watchEffect } from "vue";
import CountryFlag from "vue-country-flag-next";
import { createI18n } from "vue-i18n";
import type { NavigationGuardNext, RouteLocation } from "vue-router";
import VueSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import VueTippy, { setDefaultProps } from "vue-tippy";
import Vue3Toasity, { toast } from "vue3-toastify";
import "vue3-toastify/dist/index.css";
import { vAutofocus } from "./directives/autofocus";
import { vClick2Select } from "./directives/click2Select";
import { vNewflag } from "./directives/newflag";
import { stickyTopShadow } from "./directives/stickyTopShadow";
import { vTooltip } from "./directives/tooltip";
import { storeAnimatedText } from "./store/animated_text/storeAnimatedText";
import { storeAPI } from "./store/api/storeAPI";
import { storeBluesky } from "./store/bluesky/storeBluesky";
import { storeChatPoll } from "./store/chat_poll/storeChatPoll";
import { storeCommon } from "./store/common/storeCommon";
import { storeCustomTrain } from "./store/customtrain/storeCustomTrain";
import { storeDonationGoals } from "./store/donation_goals/storeDonationGoals";
import { storeElevenLabs } from "./store/elevenlabs/storeElevenLabs";
import { storeEndingCredits } from "./store/ending_credits/storeEndingCredits";
import { storeExporter } from "./store/exporter/storeExporter";
import { storeGroq } from "./store/groq/storeGroq";
import { storeLabels } from "./store/labels/storeLabels";
import { storeMeldStudio } from "./store/meldstudio/storeMeldStudio";
import { storeMixitup } from "./store/mixitup/storeMixitup";
import { storePlayability } from "./store/playability/storePlayability";
import { storeQuiz } from "./store/quiz/storeQuiz";
import { storeSammi } from "./store/sammi/storeSammi";
import { storeStreamerbot } from "./store/streamerbot/storeStreamerbot";
import { storeStreamfog } from "./store/streamfog/storeStreamfog";
import { storeStreamSocket } from "./store/streamsocket/storeStreamSocket";
import { storeTiktok } from "./store/tiktok/storeTiktok";
import { storeTiltify } from "./store/tiltify/storeTiltify";
import { storeTwitchCharity } from "./store/twitch_charity/storeTwitchCharity";
import { storeTwitchBot } from "./store/twitchbot/storeTwitchBot";
import Config from "./utils/Config";
import PerfWatchdog from "./utils/PerfWatchdog";
import {
	configureI18n,
	ORDINAL_CATEGORIES,
	type OrdinalLabels,
} from "./utils/PlaceholderModifiers";
import Utils from "./utils/Utils";

window.setInitMessage("Booting app...");

setDefaultProps({
	theme: "twitchat",
	animation: "scale",
	duration: 100,
	allowHTML: true,
	maxWidth: 250,
});

const pinia = createPinia();
pinia.use(Sentry.createSentryPiniaPlugin({ attachPiniaState: false }));
gsap.registerPlugin(ScrollToPlugin, CustomEase, CSSPlugin);
DataStore.init();

let lang: string = navigator.language || (<any>navigator)["userLanguage"];
lang = lang.substring(0, 2).toLowerCase();
const sLang = DataStore.get(DataStore.LANGUAGE);
if (sLang) lang = sLang;
const i18n = createI18n<false>({
	locale: lang,
	fallbackLocale: "en",
	legacy: false,
	globalInjection: true,
	warnHtmlMessage: false,
	fallbackWarn: Config.instance.IS_PROD,
	missingWarn: Config.instance.IS_PROD,
});

//Load labels before everything else so they are available when
//initializing stores data
void (async () => {
	try {
		window.setInitMessage("loading labels");
		const labelsRes = await fetch("/labels.json?v=" + import.meta.env.PACKAGE_VERSION);
		const labelsJSON = await labelsRes.json();
		for (const lang in labelsJSON) {
			i18n.global.setLocaleMessage(lang, labelsJSON[lang]);
		}
	} catch (error) {
		console.log(error);
		window.setTimeout(() => {
			StoreProxy.default.common.alert("An error occured when loading labels :(");
			storeMain().initComplete = true;
		}, 1000);
	}
	buildApp();
})();

function buildApp() {
	/**
	 * Add route guards for login
	 */
	router.beforeEach(async (to: RouteLocation, from: RouteLocation, next: NavigationGuardNext) => {
		const sMain = StoreProxy.default.main;
		const sAuth = StoreProxy.default.auth;
		const needAuth = to.meta.needAuth !== false;
		const needAdmin = to.meta.needAdmin === true;
		const transparent = to.meta.noBG;
		if (transparent) {
			document.body.style.backgroundColor = "transparent";
		}

		//If landing on homepage, redirect to chat if an auth token is available
		const authToken = DataStore.get(DataStore.TWITCH_AUTH_TOKEN);
		if (authToken && to.name === "home") {
			next({ name: "chat" });
			return;
		}

		if (!sMain.initComplete) {
			try {
				await new Promise((resolve) => {
					void sMain.startApp(needAuth, resolve);
				});
			} catch (error) {
				console.log(error);
			}
		}

		if (!sAuth.authenticated) {
			//Not authenticated, reroute to login
			if (needAuth && to.name != "login" && to.name != "logout" && to.name != "oauth") {
				next({ name: "login", params: { redirect: to.fullPath } });
				return;
			}
		}

		//Not admin, reroute to login
		if (needAdmin && !StoreProxy.default.auth.isAdmin) {
			next({ name: "home" });
			return;
		}

		next();
	});

	/**
	 * Opens up a confirm window so the user can confirm or cancel an action.
	 */
	const confirm = <T>(
		title: string,
		description?: string,
		data?: T,
		yesLabel?: string,
		noLabel?: string,
	): Promise<T | undefined> => {
		return StoreProxy.default.main.confirm(title, description, data, yesLabel, noLabel);
	};

	/**
	 * Global helper to place a dropdown list
	 */
	const placeDropdown = (
		dropdownList: HTMLDivElement,
		component: { $refs: { [key: string]: HTMLElement } },
		params: { width: string; left: string; top: string },
	) => {
		dropdownList.style.width = params.width;
		const popper = createPopper(component.$refs.toggle!, dropdownList, { placement: "top" });
		return () => popper.destroy();
	};

	window.setInitMessage("Building stores");

	const app = createApp(App).use(pinia);

	//Init stores before instanciating the router because the
	//router needs to access some stores
	StoreProxy.default.router = router;
	StoreProxy.default.i18n = i18n.global;
	//Let the placeholder modifiers localize their output.
	//Done within a watchEffect so a language change or a labels reload
	//is pushed to them without having to think about it.
	watchEffect(() => {
		const ordinals: OrdinalLabels = {};
		for (const category of ORDINAL_CATEGORIES) {
			const key = "global.ordinal." + category;
			if (i18n.global.te(key)) ordinals[category] = i18n.global.t(key);
		}
		configureI18n(i18n.global.locale.value, ordinals);
	});
	// oxlint-disable-next-line typescript/unbound-method
	StoreProxy.default.asset = Utils.asset;
	StoreProxy.default.main = storeMain();
	StoreProxy.default.auth = storeAuth();
	StoreProxy.default.automod = storeAutomod();
	StoreProxy.default.bingo = storeBingo();
	StoreProxy.default.bingoGrid = storeBingoGrid();
	StoreProxy.default.chat = storeChat();
	StoreProxy.default.chatSuggestion = storeChatSuggestion();
	StoreProxy.default.emergency = storeEmergency();
	StoreProxy.default.music = storeMusic();
	StoreProxy.default.obs = storeOBS();
	StoreProxy.default.params = storeParams();
	StoreProxy.default.poll = storePoll();
	StoreProxy.default.chatPoll = storeChatPoll();
	StoreProxy.default.prediction = storePrediction();
	StoreProxy.default.raffle = storeRaffle();
	StoreProxy.default.rewards = storeRewards();
	StoreProxy.default.stream = storeStream();
	StoreProxy.default.timers = storeTimer();
	StoreProxy.default.triggers = storeTriggers();
	StoreProxy.default.tts = storeTTS();
	StoreProxy.default.users = storeUsers();
	StoreProxy.default.voice = storeVoice();
	StoreProxy.default.debug = storeDebug();
	StoreProxy.default.accessibility = storeAccessibility();
	StoreProxy.default.admin = storeAdmin();
	StoreProxy.default.counters = storeCounters();
	StoreProxy.default.heat = storeHeat();
	StoreProxy.default.patreon = storePatreon();
	StoreProxy.default.youtube = storeYoutube();
	StoreProxy.default.values = storeValues();
	StoreProxy.default.account = storeAccount();
	StoreProxy.default.extension = storeExtension();
	StoreProxy.default.qna = storeQna();
	StoreProxy.default.discord = storeDiscord();
	StoreProxy.default.streamlabs = storeStreamlabs();
	StoreProxy.default.streamelements = storeStreamelements();
	StoreProxy.default.kofi = storeKofi();
	StoreProxy.default.lumia = storeLumia();
	StoreProxy.default.tipeee = storeTipeee();
	StoreProxy.default.common = storeCommon();
	StoreProxy.default.donationGoals = storeDonationGoals();
	StoreProxy.default.tiltify = storeTiltify();
	StoreProxy.default.tiktok = storeTiktok();
	StoreProxy.default.streamerbot = storeStreamerbot();
	StoreProxy.default.sammi = storeSammi();
	StoreProxy.default.labels = storeLabels();
	StoreProxy.default.mixitup = storeMixitup();
	StoreProxy.default.twitchCharity = storeTwitchCharity();
	StoreProxy.default.elevenLabs = storeElevenLabs();
	StoreProxy.default.playability = storePlayability();
	StoreProxy.default.twitchBot = storeTwitchBot();
	StoreProxy.default.animatedText = storeAnimatedText();
	StoreProxy.default.customTrain = storeCustomTrain();
	StoreProxy.default.streamSocket = storeStreamSocket();
	StoreProxy.default.exporter = storeExporter();
	StoreProxy.default.groq = storeGroq();
	StoreProxy.default.endingCredits = storeEndingCredits();
	StoreProxy.default.quiz = storeQuiz();
	StoreProxy.default.streamfog = storeStreamfog();
	StoreProxy.default.api = storeAPI();
	StoreProxy.default.bluesky = storeBluesky();
	StoreProxy.default.meldStudio = storeMeldStudio();

	const keys = Object.keys(StoreProxy.default);
	keys.forEach((k) => {
		if (!StoreProxy.default[k as keyof typeof StoreProxy.default]) {
			//Not necessary for main app
			if (k == "public") return;
			console.error('ERROR !! StoreProxy "' + k + '" not initialized !');
		}
	});

	window.setInitMessage("Building interface");
	app.use(router)
		.use(i18n)
		.use(ContextMenu)
		.use(VueTippy, {
			//vue-tippy's directive is replaced by vTooltip that builds tooltips lazily
			directive: "tippy",
			component: "tooltip",
		})
		.use(Vue3Toasity, {
			autoClose: 5_000,
			position: toast.POSITION.BOTTOM_RIGHT,
		})
		.component("country-flag", CountryFlag)
		.component("vue-select", VueSelect)
		.component("Icon", Icon)
		.directive("tooltip", vTooltip)
		.directive("stickyTopShadow", stickyTopShadow)
		.directive("autofocus", vAutofocus)
		.directive("click2Select", vClick2Select)
		.directive("newflag", vNewflag);
	// oxlint-disable-next-line typescript/unbound-method
	app.config.globalProperties.$asset = Utils.asset;
	app.config.globalProperties.$utils = Utils;
	app.config.globalProperties.$config = Config.instance;
	app.config.globalProperties.$confirm = confirm;
	app.config.globalProperties.$placeDropdown = placeDropdown;
	app.config.globalProperties.$store = StoreProxy.default;

	window.addEventListener("beforeinstallprompt", (e: Event) => {
		e.preventDefault();
		StoreProxy.default.main.setAhsInstaller(e as TwitchatDataTypes.InstallHandler);
	});

	if (Config.instance.IS_PROD) {
		Sentry.init({
			debug: false,
			release: "twitchat@" + import.meta.env.PACKAGE_VERSION,
			dsn: Config.instance.SENTRY_DSN,
			integrations: (defaults) => [
				// Drop vue integration that adds a mixin to all components for no
				// create use today
				...defaults.filter((integration) => integration.name !== "Vue"),
				Sentry.webVitalsIntegration(),
				Sentry.reportingObserverIntegration(),
				Sentry.replayIntegration({
					maskAllText: true,
					blockAllMedia: false,
				}),
			],
			environment: Config.instance.SENTRY_ENVIRONMENT,
			initialScope: { tags: { app: "main" } },
			tracesSampleRate: 1.0,
			replaysSessionSampleRate: 0,
			replaysOnErrorSampleRate: 0,
			ignoreErrors: [
				"[-]", //Custom tag to ignore errors coming from specific parts of the app
				"reading 'innerText'", //When emptying a content-editable field
				"venmo", //When opening paypal popup
				"Detected popup close", //When closing paypal popup
				"OBS is not ready", //If trying to connect to OBS when OBS-ws is booting
				"This is likely a Vue internals bug", //Dev potential exception
				"Connection error", //websocket connection attempts
			],
		});
		Sentry.attachErrorHandler(app);
	}

	// log failed routes to sentry
	router.onError((error: unknown) => {
		console.error(error);
		Sentry.captureException(error);
	});

	if (Config.instance.BETA_MODE) PerfWatchdog.instance.start();

	window.setInitMessage("Mounting interface");
	app.mount("#app");

	document.addEventListener(
		"keyup",
		(e: KeyboardEvent) => {
			//Given a Sentry error, a user apparently succeeded to have an
			//"undefined" e.key value on an up to date Edge browser
			if (!e.key) return;

			const metaKey = e.metaKey || e.ctrlKey;

			//Reload labels on CTRL+Alt+L
			if (e.key.toLowerCase() == "l" && metaKey && e.altKey) {
				void StoreProxy.default.main.reloadLabels();
				e.preventDefault();
			}

			//Toggle light/dark mode on CTRL+Alt+K
			if (e.key.toLowerCase() == "k" && metaKey && e.altKey) {
				StoreProxy.default.main.toggleTheme();
				e.preventDefault();
			}

			//Walk through available locales on CTRL+Alt+M
			if (e.key.toLowerCase() == "m" && metaKey && e.altKey) {
				const locales = i18n.global.availableLocales;
				i18n.global.locale.value =
					locales[(locales.indexOf(i18n.global.locale.value) + 1) % locales.length]!;
				DataStore.set(DataStore.LANGUAGE, i18n.global.locale.value);
				e.preventDefault();
			}

			//Toggle premium/non-premium state on CTRL+Alt+P
			if (
				e.key.toLowerCase() == "p" &&
				metaKey &&
				e.altKey &&
				StoreProxy.default.auth.twitch.user.is_admin === true &&
				!Config.instance.IS_PROD
			) {
				StoreProxy.default.auth.premiumType =
					StoreProxy.default.auth.premiumType === "" ? "lifetime" : "";
				e.preventDefault();
			}
		},
		true,
	);
}
